import { useAsyncEffect } from '@/hooks';
import style from '@/less/main.less';
import { Button, Form, FormField, FormGroup, Icon, Modal, Select } from '@/semantic-ui/components';
import { useStores } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorMessage, Formik } from 'formik';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-semantic-toasts';
import { number, object } from 'yup';

interface OnboardingSubmissionDetailsSyncConfirmationModalProps {
  open: boolean;
  onClose: () => void;
}

export const OnboardingSubmissionDetailsSyncConfirmationModal = observer(
  ({ open, onClose }: OnboardingSubmissionDetailsSyncConfirmationModalProps) => {
    const { employeeStore, temporaryEmployeeStore, departmentStore, areaStore } = useStores();
    const navigate = useNavigate();

    useAsyncEffect(async () => {
      await Promise.all([departmentStore.fetchAll(), areaStore.fetchAll()]);
    }, []);

    return (
      <Modal
        className={style.modal}
        open={open}
        size="tiny"
        closeIcon={<Icon name="close" size="tiny" color="black" />}
        closeOnDimmerClick={false}
        onClose={onClose}
      >
        <Formik
          initialValues={{ departmentId: 0, areaId: 0 }}
          validationSchema={object().shape({
            departmentId: number().required('Department is required'),
            areaId: number().required('Area is required'),
          })}
          onSubmit={async ({ departmentId, areaId }) => {
            if (temporaryEmployeeStore.current) {
              await temporaryEmployeeStore.sync({
                id: temporaryEmployeeStore.current.id,
                areaId,
                departmentId,
              });

              // Make sure the new employees are re-fetched so the new employee shows up in the store
              employeeStore.clear();
              // Clear the employeeNames so the EmployeeDropdownFilter re-fetches employee names
              employeeStore.setEmployeeNames([]);

              onClose();
              toast({
                type: 'success',
                icon: 'check circle',
                title: 'Onboarding Submission Approved',
                description: 'The onboarding submission has been synced.',
                time: 5000,
              });

              navigate('/onboarding/submissions/active');
            }
          }}
        >
          {({ values, setFieldValue, handleSubmit, handleBlur }) => (
            <>
              <Modal.Header>Sync Onboarding Submission</Modal.Header>
              <Modal.Content>
                <Modal.Description style={{ marginBottom: 18 }}>
                  Please select the area and department for this employee in order to sync them to
                  the system. Once you click "sync submission", the employee will be able to use the
                  SOL mobile app.
                </Modal.Description>

                <Form>
                  <FormGroup>
                    <FormField width={16}>
                      <label htmlFor="departmentId">Department</label>
                      <Select
                        id="departmentId"
                        placeholder="Select a department"
                        options={departmentStore.values.map(({ id, label, description }) => ({
                          key: id,
                          value: id,
                          text: `${description} (${label})`,
                        }))}
                        onChange={(_, data) => setFieldValue('departmentId', data.value)}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        name="departmentId"
                        render={(err) => <span style={{ color: 'red', fontSize: 13 }}>{err}</span>}
                      />
                    </FormField>
                  </FormGroup>

                  <FormGroup>
                    <FormField width={16}>
                      <label htmlFor="areaId">Area</label>
                      <Select
                        id="areaId"
                        placeholder="Select an area"
                        options={areaStore.values.map(({ id, label, description }) => ({
                          key: id,
                          value: id,
                          text: `${description} (${label})`,
                        }))}
                        onChange={(_, data) => setFieldValue('areaId', data.value)}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        name="areaId"
                        render={(err) => <span style={{ color: 'red', fontSize: 13 }}>{err}</span>}
                      />
                    </FormField>
                  </FormGroup>
                </Form>
              </Modal.Content>

              <Modal.Actions>
                <Button color="grey" basic style={{ fontSize: '0.9rem' }} onClick={onClose}>
                  Cancel
                </Button>
                <Button color="blue" style={{ fontSize: '0.9rem' }} onClick={() => handleSubmit()}>
                  <FontAwesomeIcon icon={['fas', 'sync']} style={{ marginRight: 8 }} />
                  Sync Submission
                </Button>
              </Modal.Actions>
            </>
          )}
        </Formik>
      </Modal>
    );
  },
);
